interface SaIdUser {
  id: string;
  email: string;
  display_name?: string | null;
  role?: 'admin' | 'user';
}

interface SaIdTokenPayload {
  success?: boolean;
  data?: {
    access_token: string;
    expires_at: string;
    scope: string;
    user: SaIdUser;
  };
  error?: {
    message?: string;
  };
}

const env = (import.meta as { env?: Record<string, string | undefined> }).env;
const SAID_BASE_URL = env?.VITE_SAID_BASE_URL || 'https://accounts.sahasta.com';
const SAID_CLIENT_ID = env?.VITE_SAID_CLIENT_ID || 'fluxiplanner';
const VERIFIER_KEY = 'fluxiplanner-said-pkce-verifier';
const STATE_KEY = 'fluxiplanner-said-oauth-state';

export function saidRedirectUri(): string {
  return new URL('/auth/said/callback', window.location.origin).toString();
}

export async function startSaIdLogin(returnTo = '/teams'): Promise<void> {
  const verifier = randomVerifier();
  const state = crypto.randomUUID();
  sessionStorage.setItem(VERIFIER_KEY, verifier);
  localStorage.setItem(VERIFIER_KEY, verifier);
  sessionStorage.setItem(STATE_KEY, state);
  localStorage.setItem(STATE_KEY, state);
  sessionStorage.setItem(`${STATE_KEY}:return`, returnTo);
  localStorage.setItem(`${STATE_KEY}:return`, returnTo);

  const authorize = new URL('/authorize', SAID_BASE_URL);
  authorize.searchParams.set('client_id', SAID_CLIENT_ID);
  authorize.searchParams.set('redirect_uri', saidRedirectUri());
  authorize.searchParams.set('response_type', 'code');
  authorize.searchParams.set('state', state);
  authorize.searchParams.set('scope', 'profile email');
  authorize.searchParams.set('code_challenge_method', 'S256');
  authorize.searchParams.set('code_challenge', await codeChallenge(verifier));
  window.location.assign(authorize.toString());
}

export async function completeSaIdLogin(): Promise<{ token: string; user: SaIdUser; returnTo: string } | null> {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  if (!code) return null;

  const expectedState = sessionStorage.getItem(STATE_KEY) || localStorage.getItem(STATE_KEY);
  if (expectedState && params.get('state') !== expectedState) {
    throw new Error('SaID sign-in state did not match. Please try again.');
  }

  const verifier = sessionStorage.getItem(VERIFIER_KEY) || localStorage.getItem(VERIFIER_KEY);
  if (!verifier) throw new Error('SaID sign-in verifier was missing. Please try again.');

  const response = await fetch(new URL('/api/oauth/token', SAID_BASE_URL), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      grant_type: 'authorization_code',
      client_id: SAID_CLIENT_ID,
      redirect_uri: saidRedirectUri(),
      code,
      code_verifier: verifier,
    }),
  });
  const payload = await response.json().catch(() => null) as SaIdTokenPayload | null;
  if (!response.ok || !payload?.success || !payload.data) {
    throw new Error(payload?.error?.message || `SaID sign-in failed (${response.status})`);
  }

  const returnTo = sessionStorage.getItem(`${STATE_KEY}:return`) || localStorage.getItem(`${STATE_KEY}:return`) || '/teams';
  [VERIFIER_KEY, STATE_KEY, `${STATE_KEY}:return`].forEach(key => {
    sessionStorage.removeItem(key);
    localStorage.removeItem(key);
  });

  window.history.replaceState(null, '', '/auth/said/callback');
  return { token: payload.data.access_token, user: payload.data.user, returnTo: returnTo.startsWith('/') ? returnTo : '/teams' };
}

async function codeChallenge(verifier: string): Promise<string> {
  return base64Url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))));
}

function randomVerifier(): string {
  const bytes = new Uint8Array(32);
  crypto.getRandomValues(bytes);
  return base64Url(bytes);
}

function base64Url(bytes: Uint8Array): string {
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}
